import {
  ApiOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  BookOutlined,
  CheckCircleFilled,
  CloudServerOutlined,
  ClusterOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DollarOutlined,
  ExperimentOutlined,
  SafetyOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Card, Col, Empty, Input, Row, Space, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateToAppPath } from '../../api/app-navigation.js';
import { PageHeader } from '../../components/ui/index.js';
import { pickLocalized, tx } from '../../i18n/translate.js';
import { Icon } from '../../icons/index.js';

/**
 * AppCenterPage — 应用中心 (admin top-level)
 *
 * 定位: 未来应用扩展入口 — 插件 / 子应用 / 第三方集成 / AI Agent / BI 等
 *
 * 当前为「占位 + 路线图」UI:
 *   - 已上线模块: Sales Hub / Operations Console / Admin Settings 卡片直跳
 *   - 待上线模块: AI Agent / BI 看板 / 移动端 / MDM 等灰色卡片 (Phase 2-5)
 *   - 内置应用 / 即将上线 / 第三方集成 三 segment 切换
 *
 * 后续接入方式:
 *   - 数据源: GET /v1/apps + 自定义 manifest JSON (类似 K8s helm chart 的应用市场)
 *   - 子应用走 sitemap config + 独立路由 (Vue/React micro-frontend 都可)
 */

type AppStatus = 'live' | 'beta' | 'roadmap' | 'integration';

interface AppItem {
  key: string;
  nameZh: string;
  nameEn: string;
  descZh: string;
  descEn: string;
  icon: React.ReactNode;
  color: string;
  status: AppStatus;
  /** 内部跳转 URL (仅 live/beta 有效) */
  url?: string;
  /** 计划上线时间 (仅 roadmap) */
  eta?: string;
  /** 标签: 业务/IT/数据/AI 等 */
  tags?: string[];
}

const STATUS_META: Record<AppStatus, { labelZh: string; labelEn: string; color: string }> = {
  live: {
    labelZh: tx('hardcoded:pages.admin.appcenterpage.4c4c3d19'),
    labelEn: 'Live',
    color: 'green',
  },
  beta: {
    labelZh: tx('hardcoded:pages.admin.appcenterpage.a0409176'),
    labelEn: 'Beta',
    color: 'blue',
  },
  roadmap: {
    labelZh: tx('hardcoded:pages.admin.appcenterpage.277db141'),
    labelEn: 'Roadmap',
    color: 'default',
  },
  integration: {
    labelZh: tx('hardcoded:pages.admin.appcenterpage.ff778dc6'),
    labelEn: 'Integration',
    color: 'purple',
  },
};

const APPS: AppItem[] = [
  // ── 已上线 ──
  {
    key: 'crm',
    nameZh: '销售中心',
    nameEn: 'Sales Center',
    descZh: '客户 / 联系人 / 线索 / 商机 / 报价 / 中信保 / 项目交付 全流程',
    descEn:
      'Account / Contact / Lead / Opportunity / Quote / Sinosure / Delivery — full sales pipeline',
    icon: <DashboardOutlined />,
    color: '#0078d4',
    status: 'live',
    url: '/app/crm',
    tags: [tx('hardcoded:pages.admin.appcenterpage.70c5be29'), 'CRM'],
  },
  {
    key: 'operations',
    nameZh: '运营中心',
    nameEn: 'Operations Center',
    descZh: '运营总览 / 报告中心 / 审查中心 — 统一运营分析与风险处置',
    descEn: 'Overview / Reporting / Review — unified operations insight and risk handling',
    icon: <DatabaseOutlined />,
    color: '#10b981',
    status: 'live',
    url: '/app/operations',
    tags: [tx('hardcoded:pages.admin.appcenterpage.cb64714b')],
  },
  {
    key: 'finance',
    nameZh: '财务中心',
    nameEn: 'Finance Center',
    descZh: '客户催收 / 发函清单 / 催款函模板 — 财务回款支持',
    descEn:
      'Customer collections / letter list / collection templates — finance receivables support',
    icon: <DollarOutlined />,
    color: '#2563eb',
    status: 'live',
    url: '/app/finance',
    tags: ['Finance', 'AR'],
  },
  {
    key: 'admin',
    nameZh: 'CREST平台',
    nameEn: 'CREST Platform',
    descZh: '用户 / 角色 / BU / FSP / 集成监控 — IT 治理',
    descEn: 'User / Role / BU / FSP / Integration — IT governance',
    icon: <SafetyOutlined />,
    color: '#7c3aed',
    status: 'live',
    url: '/app/admin',
    tags: ['IT'],
  },
  // ── 规划中 ──
  {
    key: 'ai-agent',
    nameZh: 'AI 助手',
    nameEn: 'AI Agent',
    descZh: 'LangGraph + LLM Router — 自然语言查商机/写周报/起报价',
    descEn:
      'LangGraph + LLM Router — natural-language opportunity search, weekly report, quote drafting',
    icon: <Icon.AI size={20} />,
    color: '#f59e0b',
    status: 'roadmap',
    eta: tx('hardcoded:pages.admin.appcenterpage.e26f3471'),
    tags: ['AI', tx('hardcoded:pages.admin.appcenterpage.9b3cfe76')],
  },
  {
    key: 'bi-dashboard',
    nameZh: 'BI 数据看板',
    nameEn: 'BI Dashboard',
    descZh: '销售漏斗 / 区域对比 / 同比环比 — 自助式报表',
    descEn: 'Sales funnel / regional comparison / YoY MoM — self-service reporting',
    icon: <BarChartOutlined />,
    color: '#06b6d4',
    status: 'roadmap',
    eta: tx('hardcoded:pages.admin.appcenterpage.865ddfe8'),
    tags: [tx('hardcoded:pages.admin.appcenterpage.1c44ff4e')],
  },
  {
    key: 'mobile',
    nameZh: '移动端',
    nameEn: 'Mobile App',
    descZh: 'PWA / React Native — 销售员手机录入 + 拜访打卡',
    descEn: 'PWA / React Native — field sales mobile entry + check-in',
    icon: <ApiOutlined />,
    color: '#ec4899',
    status: 'roadmap',
    eta: tx('hardcoded:pages.admin.appcenterpage.0da6eb4c'),
    tags: [tx('hardcoded:pages.admin.appcenterpage.6af9d645')],
  },
  {
    key: 'mdm',
    nameZh: '主数据治理',
    nameEn: 'MDM',
    descZh: 'Splink 实体匹配 + 黄金记录回写 — 跨系统主数据一致性',
    descEn: 'Splink entity matching + golden record — cross-system MDM',
    icon: <ClusterOutlined />,
    color: '#64748b',
    status: 'roadmap',
    eta: tx('hardcoded:pages.admin.appcenterpage.0a1b4864'),
    tags: [
      tx('hardcoded:pages.admin.appcenterpage.1c44ff4e'),
      tx('hardcoded:pages.admin.appcenterpage.5a82ca6b'),
    ],
  },
  {
    key: 'knowledge-base',
    nameZh: '知识库',
    nameEn: 'Knowledge Base',
    descZh: '产品手册 / FAQ / 技术文档 — pgvector 语义搜索',
    descEn: 'Product manuals / FAQ / tech docs — pgvector semantic search',
    icon: <BookOutlined />,
    color: '#0ea5e9',
    status: 'roadmap',
    eta: tx('hardcoded:pages.admin.appcenterpage.e26f3471'),
    tags: ['AI', tx('hardcoded:pages.admin.appcenterpage.b4747a9c')],
  },
  // ── 第三方集成 (后续可挂载) ──
  {
    key: 'sap-integration',
    nameZh: 'SAP 集成',
    nameEn: 'SAP Integration',
    descZh: '客户主数据 / 合同 / 订单同步 — node-soap 双向通道',
    descEn: 'Account master / Contract / Order sync — node-soap bi-directional',
    icon: <CloudServerOutlined />,
    color: '#003366',
    status: 'integration',
    url: '/app/crm/settings?section=integration-logs',
    tags: ['ERP'],
  },
  {
    key: 'oa-integration',
    nameZh: 'OA 协同',
    nameEn: 'OA Integration',
    descZh: '审批 待办推送 + POC 结果回写 — 已对接',
    descEn: 'Approval inbox + POC result write-back — connected',
    icon: <CheckCircleFilled />,
    color: '#10b981',
    status: 'integration',
    url: '/app/crm/settings?section=integration-logs',
    tags: [tx('hardcoded:pages.admin.appcenterpage.5a761676')],
  },
  {
    key: 'lab',
    nameZh: '实验室',
    nameEn: 'Lab',
    descZh: 'A/B 测试 / 灰度开关 / 业务规则沙盒',
    descEn: 'A/B testing / feature flags / business rule sandbox',
    icon: <ExperimentOutlined />,
    color: '#a855f7',
    status: 'roadmap',
    eta: 'Phase 4',
    tags: [tx('hardcoded:pages.admin.appcenterpage.567ff3c1')],
  },
];

const HIDE_OPERATIONS_APP = import.meta.env.VITE_HIDE_OPERATIONS_APP === 'true';

export function AppCenterPage() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const visibleApps = useMemo(() => {
    return APPS.filter((app) => {
      if (HIDE_OPERATIONS_APP && app.key === 'operations') return false;
      return true;
    });
  }, []);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return visibleApps;
    return visibleApps.filter(
      (a) =>
        a.nameZh.toLowerCase().includes(q) ||
        a.nameEn.toLowerCase().includes(q) ||
        a.descZh.toLowerCase().includes(q) ||
        a.descEn.toLowerCase().includes(q) ||
        (a.tags ?? []).some((t) => t.toLowerCase().includes(q)),
    );
  }, [keyword, visibleApps]);

  const groups: Array<{ status: AppStatus; items: AppItem[] }> = [
    { status: 'live', items: filtered.filter((a) => a.status === 'live') },
    { status: 'beta', items: filtered.filter((a) => a.status === 'beta') },
    { status: 'integration', items: filtered.filter((a) => a.status === 'integration') },
    { status: 'roadmap', items: filtered.filter((a) => a.status === 'roadmap') },
  ];

  return (
    <div>
      <PageHeader
        icon={<AppstoreOutlined style={{ fontSize: 22 }} />}
        title={tx('legacy:pages.admin.appcenterpage.b29cfd52')}
        subtitle={tx('legacy:dynamic.appCenterSummary', {
          total: visibleApps.length,
          live: visibleApps.filter((a) => a.status === 'live').length,
          planned: visibleApps.filter((a) => a.status === 'roadmap').length,
          integrations: visibleApps.filter((a) => a.status === 'integration').length,
        })}
        extra={
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={tx('legacy:pages.admin.appcenterpage.15013820')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            style={{ width: 280 }}
          />
        }
      />

      {filtered.length === 0 && (
        <Empty description={tx('legacy:dynamic.appCenterNoMatch', { keyword })} />
      )}

      {groups.map((g) =>
        g.items.length === 0 ? null : (
          <div key={g.status} style={{ marginBottom: 28 }}>
            <Typography.Title level={5} style={{ margin: '0 0 12px', fontSize: 14 }}>
              <Tag color={STATUS_META[g.status].color} style={{ marginRight: 8 }}>
                {pickLocalized(STATUS_META[g.status].labelZh, STATUS_META[g.status].labelEn)}
              </Tag>
              {tx('legacy:dynamic.itemCount', { count: g.items.length })}
            </Typography.Title>
            <Row gutter={[16, 16]}>
              {g.items.map((app) => (
                <Col xs={24} sm={12} lg={8} xxl={6} key={app.key}>
                  <Card
                    hoverable={!!app.url}
                    onClick={() => app.url && navigateToAppPath(app.url, navigate)}
                    style={{
                      height: '100%',
                      cursor: app.url ? 'pointer' : 'default',
                      opacity: app.status === 'roadmap' ? 0.7 : 1,
                      borderRadius: 10,
                    }}
                    styles={{ body: { padding: 16 } }}
                  >
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Space>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            background: `${app.color}20`,
                            color: app.color,
                            fontSize: 20,
                          }}
                        >
                          {app.icon}
                        </span>
                        <div style={{ flex: 1 }}>
                          <Typography.Text strong style={{ fontSize: 15 }}>
                            {pickLocalized(app.nameZh, app.nameEn)}
                          </Typography.Text>
                          {app.eta && (
                            <Typography.Text
                              type="secondary"
                              style={{ display: 'block', fontSize: 11 }}
                            >
                              {tx('legacy:pages.admin.appcenterpage.29849f31')}: {app.eta}
                            </Typography.Text>
                          )}
                        </div>
                      </Space>
                      <Typography.Paragraph
                        type="secondary"
                        style={{ fontSize: 12, margin: 0, minHeight: 36 }}
                        ellipsis={{ rows: 2, tooltip: pickLocalized(app.descZh, app.descEn) }}
                      >
                        {pickLocalized(app.descZh, app.descEn)}
                      </Typography.Paragraph>
                      {(app.tags ?? []).length > 0 && (
                        <Space size={4} wrap>
                          {app.tags?.map((t) => (
                            <Tag key={t} style={{ margin: 0, fontSize: 11 }}>
                              {t}
                            </Tag>
                          ))}
                        </Space>
                      )}
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        ),
      )}

      <Card
        size="small"
        style={{ marginTop: 8, background: '#fafafa', borderStyle: 'dashed' }}
        styles={{ body: { padding: 16 } }}
      >
        <Space direction="vertical" size={4}>
          <Typography.Text strong>
            {tx('legacy:pages.admin.appcenterpage.c1f369f8')}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {tx('legacy:pages.admin.appcenterpage.9eb59229')}
          </Typography.Text>
        </Space>
      </Card>
    </div>
  );
}
